import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { acceptTerms } from '../../services/registration';
import { useTranslation } from 'react-i18next';
import { setAccountId, getAccountId, setPartnerInfo, getPartner, setError } from '../../utils/storage';
import { handleApiError } from '../../utils/apiError';
import PageLoaderOverlay from '../../components/loading/PageLoaderOverlay';
import ReCAPTCHA from 'react-google-recaptcha';
import {
    AnalyticsEvent,
    markRegistrationStarted,
    setAnalyticsCustomerType,
    trackRegistrationFailed,
    useAnalytics,
} from '../../analytics';
import useLogger from '../../hooks/useLogger';
import { TERMS_ACTIONS, LOG_STATUSES, RECAPTCHA_ACTIONS } from '../../logging/LoggingConstants';
import { getConfigValue } from '../../utils/env';
import { isAllowedPartnerRedirect } from '../../utils/safeRedirect';
import { parsePartnerPrefill } from '../../utils/partnerValidation';
import { useCurrentLang } from '../../hooks/useCurrentLang';
import PageHeader from '../../components/PageHeader';
import CustomButton from '../../components/customButton/CustomButton';
import Checkbox from '../../components/form/Checkbox';
import RegistrationPage, { FORM_CONTENT_CLASS } from '../../components/layouts/RegistrationPage';
import { cn } from '../../utils/cn';

const urls = {
    en: 'https://ardshinbank.am/Information/personal-data-processing-terms?lang=en',
    ru: 'https://ardshinbank.am/Information/personal-data-processing-terms?lang=ru',
    am: 'https://ardshinbank.am/Information/personal-data-processing-terms?lang=hy',
};

export default function TermsPanel() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const { log } = useLogger();
    const { track } = useAnalytics();
    const lng = useCurrentLang();

    const [checked, setChecked] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isLoading) {
            return;
        }

        setIsLoading(true);
        let submitPhase = false;

        try {
            const token = await recaptchaRef.current?.executeAsync();
            recaptchaRef.current?.reset();

            if (!token) {
                throw new Error('reCAPTCHA verification failed');
            }

            log(RECAPTCHA_ACTIONS.RECAPTCHA_SUCCESS, LOG_STATUSES.SUCCESS);

            const body: { terms: number; partnerRedirectUrl?: string } = { terms: 1 };
            const partnerRedirectUrl = searchParams.get('redirectUrl');
            if (partnerRedirectUrl && isAllowedPartnerRedirect(partnerRedirectUrl)) {
                body.partnerRedirectUrl = partnerRedirectUrl;
                setPartnerInfo({
                    ...(getPartner() ?? {}),
                    partnerRedirectUrl,
                });
            }

            submitPhase = true;
            const response = await acceptTerms(body, token);
            setAccountId(response.accountId);
            setAnalyticsCustomerType(response.customerType);
            markRegistrationStarted();
            log(TERMS_ACTIONS.ACCEPT_TERMS, LOG_STATUSES.SUCCESS);
            track(AnalyticsEvent.TERMS_ACCEPTED);
            navigate('/info');
        } catch (error: unknown) {
            recaptchaRef.current?.reset();

            if (submitPhase) {
                log(TERMS_ACTIONS.ACCEPT_TERMS, LOG_STATUSES.FAILURE);
                // No catalog step for terms — only fail after application_id exists
                if (getAccountId()) {
                    trackRegistrationFailed({
                        step: 'registration_form',
                        error,
                    });
                }
                handleApiError(error, navigate);
            } else {
                log(RECAPTCHA_ACTIONS.RECAPTCHA_FAILURE, LOG_STATUSES.FAILURE);
                setError(error instanceof Error ? error.message : 'reCAPTCHA verification failed');
                navigate('/error');
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const partnerParam = searchParams.get('partner');
        if (partnerParam) {
            try {
                const partnerPrefillData = JSON.parse(JSON.parse(atob(partnerParam)));
                const validated = parsePartnerPrefill(partnerPrefillData);
                if (validated.success) {
                    setPartnerInfo(validated.data);
                }
            } catch {
                // Malformed partner query — skip prefill
            }
        }
    }, [searchParams]);

    return (
        <>
            <PageLoaderOverlay active={isLoading} />
            <RegistrationPage
                contentClassName={cn(FORM_CONTENT_CLASS, 'xl:max-w-[23rem] flex-1 mt-6')}
                contentWrapperClassName='flex-1'
                stickyFooter
                footer={
                    <CustomButton
                        type="submit"
                        form="terms-form"
                        disabled={!checked}
                        className="mt-6 md:mt-10 md:w-fit"
                    >
                        {t('conditionsButton')}
                    </CustomButton>
                }
            >
                <PageHeader title={t('landingHeadline')} lead={t('landingLead')} align="start"  />
                <form onSubmit={handleCreate} id="terms-form" className='mt-auto md:mt-0'>
                    <Checkbox
                        name="terms"
                        checked={checked}
                        onChange={setChecked}
                        label={
                            <>
                                {t('agreedTo')}{' '}
                                <a
                                    href={urls[lng] || urls.am}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ml-px font-semibold text-primary underline decoration-primary"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {t('terms')}
                                </a>
                            </>
                        }
                    />
                    <ReCAPTCHA
                        sitekey={getConfigValue('REACT_APP_SITE_KEY')}
                        size="invisible"
                        ref={recaptchaRef}
                    />
                </form>
            </RegistrationPage>
        </>
    );
}
